import _ from 'lodash';
import { AlbumConfigConsumer } from 'contexts/AlbumConfigProvider';
import { UserPreferencesConsumer } from 'contexts/UserPreferencesProvider';
import { useAuth0 } from '@auth0/auth0-react';
import {
  MORE_PAGES_PRICING,
  BOOK_SIZES,
  PATH_CREATOR_ABOUT
} from 'constants';
import AppDialog from 'components/Dialog/AppDialog';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import Carousel from 'react-material-ui-carousel';
import Chip from '@mui/material/Chip';
import classNames from 'classnames';
import Grid from '@mui/material/Grid';
import Image from 'components/Image';
import LoadingButton from '@mui/lab/LoadingButton';
import makeClasses from 'hooks/useClasses';
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import styles from 'styles/jss/components/bookSizeDialogStyle';
import Typography from '@mui/material/Typography';
import useAlbumAdapter from 'adapters/albumAdapter';

const slides = [
  { src: '/img/marketing/book_sizes.jpg', fit: 'cover' },
  { src: '/img/marketing/feat_carousel_yellow_book.jpg', fit: 'cover' },
  { src: '/img/marketing/feat_carousel_holding_book.jpg', fit: 'cover' }
];

const ProductSelectDialog = ({ open, handleClose, forceCreate }) => {
  const classes = makeClasses(styles);
  const router = useRouter();
  const [productId, setProductId] = useState();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { createProject, getProject } = useAlbumAdapter();
  const { isAuthenticated, loginWithRedirect } = useAuth0();
  const { project } = AlbumConfigConsumer();
  const { setAppState } = UserPreferencesConsumer();

  useEffect(() => {
    if (isAuthenticated) {
      getProject();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    // If a project is already created, highlight current product
    setProductId(_.get(project, 'product.id'));
  }, [project]);

  const handleClick = async (id) => {
    // Immediately toggle the selector
    setProductId(id);
  };

  const handleSubmit = async (event, id = productId) => {
    if (isAuthenticated) {
      setIsSubmitting(true);
      // Immediately toggle the selector
      const newProject = await createProject(id, forceCreate);
      router.push({
        pathname: PATH_CREATOR_ABOUT,
        query: { project: newProject.uuid }
      });
    } else {
      // We can't use the loginWithRedirect{ appState })
      // because we're not using the onRedirectCallback (which is utilizing localStorage anyway)
      setAppState('authState', {
        productId,
        returnTo: `${PATH_CREATOR_ABOUT}?productId=${productId}`
      });
      loginWithRedirect();
    }
  };

  return (
    <AppDialog
      open={open}
      handleClose={() => handleClose(false)}
      fullWidth
      submitLabel="Build your book"
      submitDisabled={!productId}
      title="Select a Book Size"
      buttons={[
        <LoadingButton
          key="submit"
          variant="contained"
          size="large"
          sx={{ width: '100%' }}
          loading={isSubmitting}
          onClick={handleSubmit}
          disabled={!productId}
        >
          Build your book
        </LoadingButton>
      ]}
    >
      <Grid item xs={12}>
        <Carousel
          interval={4000}
          className={classes.carousel}
          indicators={false}
        >
          {slides.map((slide) => (
            <Box
              key={slide.src}
              className="carouselSlide"
            >
              <Image
                src={slide.src}
                alt="Scribble book sizes"
                layout="fill"
                objectFit={slide.fit}
              />
            </Box>
          ))}
        </Carousel>
        <Grid
          container
          className={classes.bookTiles}
          spacing={3}
        >
          {BOOK_SIZES.map((size) => (
            <Grid item xs={12} key={size.id}>
              <Card
                variant="outlined"
                className={classNames(classes.bookTile, {
                  active: productId === size.id
                })}
              >
                <CardActionArea onClick={() => handleClick(size.id)}>
                  <Typography component="h1">
                    {size.label}
                    {size.popular && (
                      <Chip
                        label="Popular"
                        className="popularChip"
                        color="primary"
                        size="small"
                        sx={{ ml: 2 }}
                      />
                    )}
                  </Typography>
                  <Typography className="description">
                    {size.description}
                  </Typography>
                  <Typography className="pricing">
                    Starts at
                    {' '}
                    <strong>
                      &#36;
                      {size.price}
                      *
                    </strong>
                  </Typography>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
        <Typography variant="small" sx={{ pt: 2, display: 'block' }}>
          {`*${MORE_PAGES_PRICING}`}
        </Typography>
      </Grid>
    </AppDialog>
  );
};

ProductSelectDialog.defaultProps = {
  forceCreate: false
};

ProductSelectDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  forceCreate: PropTypes.bool
};

export default ProductSelectDialog;
